import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { CircleHelp } from "lucide-react"
import { z } from "zod"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Note } from "@/components/automations/primitives"
import { RoutePending } from "@/components/app/states"
import { Metrics } from "@/components/automations/primitives"
import { toMetrics } from "@/components/reports/helpers"
import { DefinitionsCard, ReportChart, ReportFilters, ReportHeader } from "@/components/reports/primitives"
import { supportReportQuery } from "@/lib/queries"
import { useWorkspaceContext } from "@/lib/session"

const Search = z.object({ period: z.enum(["7", "30"]).optional(), channel: z.enum(["all", "instagram", "messenger"]).optional() })

export const Route = createFileRoute("/w/$workspace/reports/support")({ validateSearch: Search, component: SupportReport })

function duration(seconds: number | null) {
  if (seconds === null) return "Unavailable"
  if (seconds < 60) return `${Math.round(seconds)}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`
  return `${(seconds / 86400).toFixed(1)}d`
}

/** Screen 36. */
function SupportReport() {
  const { workspace } = useWorkspaceContext()
  const { period = "7", channel = "all" } = Route.useSearch()
  const navigate = Route.useNavigate()
  const report = useQuery({ ...supportReportQuery(workspace.slug, { period, channel }), placeholderData: (p) => p })
  const data = report.data
  const set = (patch: { period?: string; channel?: string }) => void navigate({ search: (prev) => ({ ...prev, ...patch }) as never })
  return (
    <div className="page-wrap auto-page">
      <ReportHeader title="Support performance" description="Understand what’s working and where your team can help." workspace={workspace.slug} exportPath={`/api/w/${workspace.slug}/reports/support.csv?period=${period}&channel=${channel}`} exportName="support">
        <ReportFilters period={period} channel={channel} onPeriod={(v) => set({ period: v })} onChannel={(v) => set({ channel: v })} />
      </ReportHeader>
      {!data ? (
        <RoutePending />
      ) : (
        <>
          <Metrics items={toMetrics(data.metrics)} />
          <div className="auto-report-grid mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <CardTitle>Conversation volume</CardTitle>
                  <Badge variant="outline">
                    <span className="size-1.5 rounded-full bg-foreground" />
                    New conversations
                  </Badge>
                </div>
                <CardDescription>New conversations by day</CardDescription>
              </CardHeader>
              <CardContent>
                <ReportChart title="New conversations" values={data.series.map((s) => s.value)} labels={data.series.map((s) => s.label)} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Response and resolution</CardTitle>
                <CardDescription>How quickly people hear from your team.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Measure</TableHead>
                      <TableHead>Median</TableHead>
                      <TableHead>P90</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="py-5">First human reply</TableCell>
                      <TableCell>{duration(data.response.firstHumanReplyMedianSeconds)}</TableCell>
                      <TableCell>{duration(data.response.firstHumanReplyP90Seconds)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="py-5">Time to resolution</TableCell>
                      <TableCell>{duration(data.response.resolutionMedianSeconds)}</TableCell>
                      <TableCell>{duration(data.response.resolutionP90Seconds)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <div className="mt-7 flex justify-between text-sm">
                  <span className="text-muted-foreground">Customer satisfaction</span>
                  <Badge variant="secondary">Not configured</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="auto-report-grid mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Team breakdown</CardTitle>
                <CardDescription>By assignee, for conversations in this period.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Teammate</TableHead>
                      <TableHead>Resolved</TableHead>
                      <TableHead>Human response</TableHead>
                      <TableHead>Open at end</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.team.map((row) => (
                      <TableRow key={row.name}>
                        <TableCell className="py-4">{row.name}</TableCell>
                        <TableCell className="py-4 tabular-nums">{row.resolved}</TableCell>
                        <TableCell className="py-4">{duration(row.humanResponseMedianSeconds)}</TableCell>
                        <TableCell className="py-4 tabular-nums">{row.openAtEnd}</TableCell>
                      </TableRow>
                    ))}
                    {!data.team.length && (
                      <TableRow>
                        <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                          No assigned conversations in this period.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <Card className="bg-muted/40">
              <CardHeader>
                <CircleHelp className="mb-2 size-5" />
                <CardTitle>A response means a human response.</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-6 text-muted-foreground">{data.definitions[0]}</p>
              </CardContent>
            </Card>
          </div>
          <div className="mt-6">
            <DefinitionsCard title="Definitions in this report" definitions={data.definitions} />
          </div>
          <Note>Partial or unavailable channel signals are labeled explicitly. Exports preserve the metric definitions shown here.</Note>
        </>
      )}
    </div>
  )
}
